import { budgetPeriodFor } from "./household-consumption";

/**
 * Period chips for records and reports. Month boundaries follow the person's
 * month start day (payday 1–28), the same rule the living budget uses, so the
 * record total, the report and the budget never disagree (저점 #2·#4).
 */
export type PeriodKind =
	| "this_month"
	| "last_month"
	| "last_3_months"
	| "custom";

export interface ResolvedPeriod {
	kind: PeriodKind;
	from: Date;
	/** Exclusive end. */
	to: Date;
	label: string;
}

const KST = 9 * 60 * 60 * 1000;

function kstLabel(date: Date): string {
	const shifted = new Date(date.getTime() + KST);
	return `${shifted.getUTCMonth() + 1}월 ${shifted.getUTCDate()}일`;
}

function kstMidnight(dateKey: string): Date | null {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return null;
	const time = Date.parse(`${dateKey}T00:00:00+09:00`);
	return Number.isFinite(time) ? new Date(time) : null;
}

export function resolvePeriod(
	kind: PeriodKind,
	anchor: Date,
	monthStartDay: number,
	custom?: { from: string; to: string },
): ResolvedPeriod {
	const current = budgetPeriodFor(anchor, monthStartDay);
	let from = current.start;
	let to = current.end;
	if (kind === "last_month") {
		const previous = budgetPeriodFor(
			new Date(current.start.getTime() - 1),
			monthStartDay,
		);
		from = previous.start;
		to = previous.end;
	} else if (kind === "last_3_months") {
		let start = current.start;
		for (let step = 0; step < 2; step += 1)
			start = budgetPeriodFor(
				new Date(start.getTime() - 1),
				monthStartDay,
			).start;
		from = start;
	} else if (kind === "custom" && custom) {
		const a = kstMidnight(custom.from);
		const b = kstMidnight(custom.to);
		if (a && b) {
			const [start, end] = a <= b ? [a, b] : [b, a];
			from = start;
			to = new Date(end.getTime() + 86_400_000);
		}
	}
	const last = new Date(to.getTime() - 1);
	return { kind, from, to, label: `${kstLabel(from)} ~ ${kstLabel(last)}` };
}

export function inPeriod(
	transactedAt: string,
	period: Pick<ResolvedPeriod, "from" | "to">,
): boolean {
	const time = Date.parse(transactedAt);
	return (
		Number.isFinite(time) &&
		time >= period.from.getTime() &&
		time < period.to.getTime()
	);
}

/**
 * #29: KST days inside the period (up to today) that have no record — the
 * first places to look when the card statement total is higher.
 */
export function daysWithoutRecords(
	transactedAts: string[],
	period: Pick<ResolvedPeriod, "from" | "to">,
	now = new Date(),
): string[] {
	const recorded = new Set(
		transactedAts
			.filter((at) => inPeriod(at, period))
			.map((at) => new Date(Date.parse(at) + KST).toISOString().slice(0, 10)),
	);
	const end = Math.min(period.to.getTime(), now.getTime());
	const days: string[] = [];
	for (let time = period.from.getTime(); time < end; time += 86_400_000) {
		const key = new Date(time + KST).toISOString().slice(0, 10);
		if (!recorded.has(key)) days.push(key);
	}
	return days;
}
